import { useActionState, useReducer, useRef } from "react"
import { Navigate, useNavigate } from "react-router-dom"
import { ArrowLeft, Check, Clock, Copy, KeyRound, Lock, Shield, User } from "lucide-react"
import { toast } from "sonner"
import { AppHeader } from "@/components/dashboard/AppHeader"
import { SixDigitOtpInput } from "@/components/shared/SixDigitOtpInput"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useSession } from "@/lib/session-context"
import { cn } from "@/lib/utils"

const SESSION_TIMEOUT_OPTIONS = [
  { value: "1", label: "1 hour" },
  { value: "2", label: "2 hours" },
  { value: "4", label: "4 hours" },
  { value: "6", label: "6 hours" },
  { value: "12", label: "12 hours" },
  { value: "24", label: "24 hours" },
]

const SECURITY_STATE = {
  setup: null,
  backupCodes: [],
  verificationCode: "",
  securityCode: "",
  error: null,
  modalError: null,
  copied: false,
  isBusy: false,
  isTimeoutBusy: false,
  disableDialogOpen: false,
  regenerateDialogOpen: false,
}

function securityReducer(state, changes) {
  return { ...state, ...changes }
}

export function AccountSettingsScreen() {
  const { currentUser, updateProfile, updatePassword } = useSession()
  const navigate = useNavigate()

  if (!currentUser) return <Navigate to="/" replace />

  return (
    <main className="min-h-screen bg-background text-foreground">
      <AppHeader status="online" showNav />

      <section className="mx-auto flex w-full max-w-[800px] flex-col gap-8 p-4 duration-500 animate-in fade-in slide-in-from-bottom-4 md:p-8 lg:p-12">
        <button
          type="button"
          onClick={() => navigate("/settings")}
          className="inline-flex w-fit items-center gap-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </button>

        <HeaderBanner />
        <ProfileSection user={currentUser} updateProfile={updateProfile} />
        <PasswordSection updatePassword={updatePassword} />
        <SecuritySection user={currentUser} />
      </section>
    </main>
  )
}

function HeaderBanner() {
  return (
    <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent" />
      <div className="absolute right-0 top-0 size-64 -translate-y-1/2 translate-x-1/3 rounded-full bg-primary/10 blur-3xl" />
      <div className="relative px-8 py-10">
        <p className="mb-2 text-xs font-medium uppercase tracking-widest text-muted-foreground">
          Account Settings
        </p>
        <h2 className="mb-2 text-2xl font-semibold tracking-tight text-foreground">
          Manage Your Account
        </h2>
        <p className="text-sm text-muted-foreground">
          Update your profile, security preferences, and session settings.
        </p>
      </div>
    </div>
  )
}

function SectionHeader({ icon: Icon, title, subtitle, className = "pb-0" }) {
  return (
    <div className={`flex items-center gap-3 px-6 pt-6 ${className}`}>
      <div className="flex size-9 items-center justify-center rounded-lg border border-primary/20 bg-primary/10">
        <Icon className="size-4 text-primary" />
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  )
}

function PrimaryButton({ children, type = "submit", className, ...props }) {
  return (
    <button
      type={type}
      className={cn(
        "rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

function OutlineButton({ children, className, ...props }) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      {children}
    </button>
  )
}

function ProfileSection({ user, updateProfile }) {
  const [error, submitAction, isPending] = useActionState(async (_prev, formData) => {
    try {
      await updateProfile({
        first_name: formData.get("first_name"),
        last_name: formData.get("last_name"),
        email: formData.get("email"),
      })
      toast.success("Profile updated")
      return null
    } catch (e) {
      toast.error(e.message)
      return e.message
    }
  }, null)

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <SectionHeader icon={User} title="Profile" subtitle="Manage your personal information" />
      <form action={submitAction} className="space-y-4 p-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="first_name" className="text-xs">First name</Label>
            <Input id="first_name" name="first_name" defaultValue={user.first_name} autoComplete="off" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="last_name" className="text-xs">Last name</Label>
            <Input id="last_name" name="last_name" defaultValue={user.last_name} autoComplete="off" required />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="email" className="text-xs">Email</Label>
          <Input id="email" name="email" type="email" defaultValue={user.email} autoComplete="off" required />
          {user.unconfirmed_email ? (
            <p className="text-xs text-muted-foreground">
              Pending confirmation: {user.unconfirmed_email}
            </p>
          ) : null}
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <PrimaryButton disabled={isPending}>{isPending ? "Saving…" : "Save profile"}</PrimaryButton>
      </form>
    </div>
  )
}

function PasswordSection({ updatePassword }) {
  const formRef = useRef(null)

  const [error, submitAction, isPending] = useActionState(async (_prev, formData) => {
    try {
      await updatePassword({
        current_password: formData.get("current_password"),
        password: formData.get("password"),
        password_confirmation: formData.get("password_confirmation"),
      })
      toast.success("Password updated")
      formRef.current?.reset()
      return null
    } catch (e) {
      toast.error(e.message)
      return e.message
    }
  }, null)

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <SectionHeader icon={Lock} title="Password" subtitle="Change your password" />
      <form ref={formRef} action={submitAction} className="space-y-4 p-6">
        <div className="space-y-1.5">
          <Label htmlFor="current_password" className="text-xs">Current password</Label>
          <Input id="current_password" name="current_password" type="password" autoComplete="current-password" required />
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="password" className="text-xs">New password</Label>
            <Input id="password" name="password" type="password" autoComplete="new-password" required />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password_confirmation" className="text-xs">Confirm password</Label>
            <Input id="password_confirmation" name="password_confirmation" type="password" autoComplete="new-password" required />
          </div>
        </div>
        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        <PrimaryButton disabled={isPending}>{isPending ? "Updating…" : "Update password"}</PrimaryButton>
      </form>
    </div>
  )
}

function SecurityRow({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="flex items-center justify-between gap-4 border-t border-border/50 py-4 first:border-t-0">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex size-8 items-center justify-center rounded-md bg-muted">
          <Icon className="size-3.5 text-muted-foreground" />
        </div>
        <div>
          <p className="text-[13px] font-medium leading-tight text-foreground">{title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
        </div>
      </div>
      {children}
    </div>
  )
}

function SecuritySection({ user }) {
  const backupRemaining = user.backup_codes_remaining ?? 0
  const sessionHours = user.session_timeout_hours ?? 6
  const {
    beginTwoFactorSetup,
    confirmTwoFactor,
    disableTwoFactor,
    regenerateBackupCodes,
    updateSessionTimeout,
  } = useSession()
  const [state, setSecurityState] = useReducer(securityReducer, SECURITY_STATE)
  const {
    setup,
    backupCodes,
    verificationCode,
    securityCode,
    error,
    modalError,
    copied,
    isBusy,
    isTimeoutBusy,
    disableDialogOpen,
    regenerateDialogOpen,
  } = state

  async function handleSetup() {
    setSecurityState({ error: null, modalError: null, isBusy: true })
    try {
      setSecurityState({ setup: await beginTwoFactorSetup(), backupCodes: [] })
    } catch (e) {
      setSecurityState({ error: e.message })
    } finally {
      setSecurityState({ isBusy: false })
    }
  }

  async function handleConfirm(event) {
    event.preventDefault()
    setSecurityState({ error: null, modalError: null, isBusy: true })
    try {
      await confirmTwoFactor({ code: verificationCode })
      setSecurityState({
        backupCodes: setup.backup_codes,
        setup: null,
        verificationCode: "",
      })
      toast.success("Two-factor authentication enabled")
    } catch (e) {
      setSecurityState({ error: e.message })
    } finally {
      setSecurityState({ isBusy: false })
    }
  }

  async function handleDisable(event) {
    event.preventDefault()
    setSecurityState({ modalError: null, isBusy: true })
    try {
      await disableTwoFactor({ code: securityCode })
      setSecurityState({
        backupCodes: [],
        securityCode: "",
        disableDialogOpen: false,
        modalError: null,
      })
      toast.success("Two-factor authentication disabled")
    } catch (e) {
      setSecurityState({ modalError: e.message })
    } finally {
      setSecurityState({ isBusy: false })
    }
  }

  async function handleRegenerate(event) {
    event.preventDefault()
    setSecurityState({ modalError: null, isBusy: true })
    try {
      const result = await regenerateBackupCodes({ code: securityCode })
      setSecurityState({
        backupCodes: result.backup_codes,
        securityCode: "",
        regenerateDialogOpen: false,
        modalError: null,
      })
      toast.success("Backup codes regenerated")
    } catch (e) {
      setSecurityState({ modalError: e.message })
    } finally {
      setSecurityState({ isBusy: false })
    }
  }

  async function copyBackupCodes() {
    await navigator.clipboard.writeText(backupCodes.join("\n"))
    setSecurityState({ copied: true })
    setTimeout(() => setSecurityState({ copied: false }), 2000)
  }

  function handleDisableDialogChange(open) {
    setSecurityState({
      disableDialogOpen: open,
      securityCode: open ? securityCode : "",
      error: open ? null : error,
      modalError: null,
    })
  }

  function handleRegenerateDialogChange(open) {
    setSecurityState({
      regenerateDialogOpen: open,
      securityCode: open ? securityCode : "",
      error: open ? null : error,
      modalError: null,
    })
  }

  async function handleTimeoutChange(value) {
    const nextTimeout = Number(value)
    if (nextTimeout === sessionHours) return

    setSecurityState({ isTimeoutBusy: true })
    try {
      await updateSessionTimeout({ session_timeout_hours: nextTimeout })
      toast.success("Session timeout updated")
    } catch (e) {
      toast.error(e.message)
    } finally {
      setSecurityState({ isTimeoutBusy: false })
    }
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
      <SectionHeader
        icon={Shield}
        title="Security"
        subtitle="Manage authentication and session settings"
        className="pb-4"
      />

      <div className="space-y-0 px-6 pb-6">
        <SecurityRow
          icon={Shield}
          title="Two-factor authentication"
          subtitle="Protected with TOTP-based two-factor authentication"
        >
          <div className="flex shrink-0 items-center gap-2">
            <Badge variant="secondary">{user.otp_enabled ? "Enabled" : "Disabled"}</Badge>
            {user.otp_enabled ? null : (
              <OutlineButton onClick={handleSetup} disabled={isBusy}>Enable 2FA</OutlineButton>
            )}
          </div>
        </SecurityRow>

        {setup ? (
          <form onSubmit={handleConfirm} className="mb-4 rounded-xl border border-border bg-background p-4">
            <p className="text-sm font-semibold text-foreground">Scan QR code</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Add Sentinel to your authenticator app, then enter the generated code.
            </p>
            <div className="mt-4 flex flex-col gap-4 sm:flex-row">
              <div className="w-full max-w-[180px] rounded-lg border border-border bg-card p-2">
                <img
                  src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.qr_svg)}`}
                  alt="Two-factor setup QR code"
                  className="h-auto w-full"
                />
              </div>
              <div className="flex flex-1 flex-col gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="two_factor_setup_code" className="text-xs">Verification code</Label>
                  <SixDigitOtpInput
                    id="two_factor_setup_code"
                    value={verificationCode}
                    onChange={(nextCode) => setSecurityState({ verificationCode: nextCode })}
                    containerClassName="justify-center sm:justify-start"
                  />
                </div>
                <PrimaryButton disabled={isBusy || verificationCode.length !== 6}>
                  {isBusy ? "Verifying…" : "Verify & enable"}
                </PrimaryButton>
              </div>
            </div>
          </form>
        ) : null}

        {backupCodes.length > 0 ? (
          <div className="mb-4 rounded-xl border border-border bg-background p-4">
            <p className="text-sm font-semibold text-foreground">Save backup codes</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Store these one-time codes somewhere safe.
            </p>
            <div className="mt-3 grid grid-cols-2 gap-2 rounded-lg border border-border bg-muted p-3 font-mono text-sm">
              {backupCodes.map((code) => (
                <span key={code} className="text-center">{code}</span>
              ))}
            </div>
            <OutlineButton onClick={copyBackupCodes} className="mt-3">
              {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
              {copied ? "Copied" : "Copy codes"}
            </OutlineButton>
          </div>
        ) : null}

        <SecurityRow
          icon={KeyRound}
          title="Backup codes"
          subtitle={`${backupRemaining} of 10 codes remaining`}
        >
          <OutlineButton disabled={!user.otp_enabled} onClick={() => handleRegenerateDialogChange(true)}>
            Regenerate codes
          </OutlineButton>
        </SecurityRow>

        {error ? <p className="pb-4 text-sm text-destructive">{error}</p> : null}

        {user.otp_enabled ? (
          <>
            <SecurityRow
              icon={Lock}
              title="Disable two-factor authentication"
              subtitle="Verify with your authenticator before turning off 2FA"
            >
              <OutlineButton onClick={() => handleDisableDialogChange(true)}>
                Disable 2FA
              </OutlineButton>
            </SecurityRow>

            <Dialog open={disableDialogOpen} onOpenChange={handleDisableDialogChange}>
              <TwoFactorCodeDialog
                title="Disable two-factor authentication"
                label="Current authenticator code"
                inputId="two_factor_disable_code"
                code={securityCode}
                error={modalError}
                isBusy={isBusy}
                submitLabel="Disable"
                busyLabel="Disabling…"
                onCancel={() => handleDisableDialogChange(false)}
                onCodeChange={(nextCode) => setSecurityState({ securityCode: nextCode })}
                onSubmit={handleDisable}
              />
            </Dialog>

            <Dialog open={regenerateDialogOpen} onOpenChange={handleRegenerateDialogChange}>
              <TwoFactorCodeDialog
                title="Regenerate backup codes"
                label="Regeneration authenticator code"
                inputId="two_factor_regenerate_code"
                code={securityCode}
                error={modalError}
                isBusy={isBusy}
                submitLabel="Regenerate"
                busyLabel="Regenerating…"
                onCancel={() => handleRegenerateDialogChange(false)}
                onCodeChange={(nextCode) => setSecurityState({ securityCode: nextCode })}
                onSubmit={handleRegenerate}
              />
            </Dialog>
          </>
        ) : null}

        <SecurityRow
          icon={Clock}
          title="Session timeout"
          subtitle="Automatically sign out after a period of inactivity"
        >
          <Select
            value={String(sessionHours)}
            onValueChange={handleTimeoutChange}
            disabled={isTimeoutBusy}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SESSION_TIMEOUT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SecurityRow>
      </div>
    </div>
  )
}

function TwoFactorCodeDialog({
  title,
  label,
  inputId,
  code,
  error,
  isBusy,
  submitLabel,
  busyLabel,
  onCancel,
  onCodeChange,
  onSubmit,
}) {
  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>
          Enter the 6-digit code from your authenticator app.
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={onSubmit} className="space-y-5">
        <div className="flex flex-col items-center gap-3 py-2">
          <Label htmlFor={inputId} className="text-sm">
            {label}
          </Label>
          <SixDigitOtpInput
            id={inputId}
            ariaLabel={label}
            value={code}
            onChange={onCodeChange}
          />
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}

        <DialogFooter>
          <OutlineButton onClick={onCancel}>Cancel</OutlineButton>
          <PrimaryButton disabled={isBusy || code.length !== 6}>
            {isBusy ? busyLabel : submitLabel}
          </PrimaryButton>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
